import { Component } from '@angular/core';
import { Contested } from '../../../../enums/contested.enum';
import { Material } from '../../../../enums/material.enum';
import { UntypedFormControl } from '@angular/forms';
import { SearchManager } from '../../../../managers/search.manager';
import { Document } from '../../../../models/ui/document.model';
import { filter, map, Observable, shareReplay, switchMap } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { cloneDeep, range } from 'lodash-es';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalysisManager } from '../../../../managers/analysis.manager';
import { Fact } from '../../../../models/ui/fact.model';
import { Issue } from '../../../../models/ui/issue.model';
import { LinkedDocument } from '../../../../models/ui/linked-document.model';
import { LinkedIssue } from '../../../../models/ui/linked-issue.model';
import { BaseComponent } from '../../../base.component';
import { NotificationManager } from '../../../../managers/notification.manager';

@Component({
    selector: 'lex-fact-edit',
    templateUrl: './fact-edit.component.html',
    standalone: false
})
export class FactEditComponent extends BaseComponent {
	public contestedList = Contested.All;
	public materialList = Material.All;
	public issues$: Observable<LinkedIssue[]>;
	public documents$: Observable<Document[]>;

	public document$: Observable<Document>;
	public pageCount$: Observable<number[]>;
	public editingFact: Fact;

	public formControl = new UntypedFormControl();
	public formControlPage = new UntypedFormControl();

	constructor(
		private searchManager: SearchManager,
		private activatedRoute: ActivatedRoute,
		private analysisManager: AnalysisManager,
		private notificationManager: NotificationManager,
		private router: Router
	) {
		super();
		const targetId = this.activatedRoute.snapshot.params.id;

		if (targetId === 'new') {
			this.analysisManager.setSelectedFact({ createNewFact: true });
		} else {
			this.analysisManager.setSelectedFact({ id: targetId });
		}

		this.analysisManager.refreshIssues();

		this.issues$ = this.analysisManager.issues$.pipe(
			filter((response) => response !== null),
			map((collection) => collection.items.map((item) => {
				const newItem = new LinkedIssue();
				newItem.id = item.id;
				newItem.title = item.title;
				return newItem;
			})),
		);

		this.analysisManager.selectedFact$.pipe(
			filter((response) => response !== null),
			takeUntil(this.destroy$)
		).subscribe((fact) => {
			this.editingFact = fact;
			this.documents$ = this.formControl.valueChanges.pipe(
				debounceTime(100),
				filter((value) => typeof value === 'string'),
				switchMap((value) => {
					return this.searchManager.search({
						documentTitle : value,
						take : 20
					});
				}),
				map((result) => result.items)
			);

			this.document$ = this.formControl.valueChanges.pipe(
				filter((value) => typeof value === 'object'),
				shareReplay(1)
			);

			this.pageCount$ = this.document$.pipe(
				map((value) => {
					return value.pageCount > 0 ? range(1, value.pageCount + 1) : [1];
				})
			);
		});
	}

	public save() {
		this.analysisManager.saveFact({ fact: this.editingFact }).subscribe(() => {
			this.analysisManager.refreshFilteredIssues();
			this.router.navigate(['/', 'analytics', 'facts-and-issues']).then(() => {
				this.notificationManager.displaySnackBarMessage({ message: 'Fact saved successfully' });
			});
		});
	}

	public cancel() {
		this.router.navigate(['/', 'analytics', 'facts-and-issues']);
	}

	public selectAllIssues() {
		this.issues$.pipe(
			take(1)
		).subscribe((issues) => {
			this.editingFact.linkedIssues = cloneDeep(issues);
		});
	}

	public unSelectAllIssues() {
		this.editingFact.linkedIssues = [];
	}

	public compareIssues(obj1: Issue, obj2: Issue): boolean {
		return obj1.id === obj2.id;
	}

	public displayWithFn(document: Document) {
		return `${ document?.title ?? '' }${ document?.extension ?? '' }`;
	}

	public addDocument() {
		this.document$.pipe(take(1)).subscribe((selectedDocument) => {
			const isDocumentAlreadyLinked = this.editingFact.linkedDocuments.some((linkedDocument) =>
				linkedDocument.documentId === selectedDocument.id && linkedDocument.pageNumber === this.formControlPage.value);
			if(isDocumentAlreadyLinked) return;

			const linkedDocument = new LinkedDocument();
			linkedDocument.factId = this.editingFact.id;
			linkedDocument.documentId = selectedDocument.id;
			linkedDocument.pageNumber = this.formControlPage.value ?? 1;
			linkedDocument.documentTitle = `${ this.displayWithFn(selectedDocument) } (p. ${ linkedDocument.pageNumber })`;
			linkedDocument.caseId = this.editingFact.caseId;
			this.editingFact.linkedDocuments.push(linkedDocument);
		});
	}

	public removeDocument(target: LinkedDocument): void {
		this.editingFact.linkedDocuments = this.editingFact.linkedDocuments.filter((document) => {
			return document !== target;
		});
	}

	public issueComparatorFunction(a: LinkedIssue, b: LinkedIssue): boolean {
		return a.id === b.id;
	}
}
